import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { AuthMeResponse } from "@shared/types/auth";
import { readLoginFailure } from "./loginErrors";
import { setActingOwner } from "../http/foyer";
import { actingOwnerFor, leaveOwnerShell } from "./actingOwner";

interface AuthState {
  user: AuthMeResponse | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

/** Browser side. Polls `/api/cms/v1/auth/me` on mount, exposes login/logout. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthMeResponse | null>(null);
  /**
   * A supplier's own login acts for its own owner, and an admin for the
   * owner it had open in this tab (`actingOwner.ts`). Set HERE, synchronously,
   * not in an effect: child effects run before a parent's, so a supplier page
   * would otherwise fetch before the owner it needs is known.
   */
  const setUser = useCallback((next: AuthMeResponse | null) => {
    setActingOwner(actingOwnerFor(next));
    setUserState(next);
  }, []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/cms/v1/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setUser(data as AuthMeResponse | null);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      let res: Response;
      try {
        res = await fetch("/api/cms/v1/auth/login", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
      } catch {
        // The request never reached anything - no server, DNS, offline.
        throw new Error("server_unreachable");
      }
      if (!res.ok) {
        /**
         * Only 401 means the credentials were wrong.
         *
         * Everything else is the server failing: a 502/504 from a dev proxy with
         * nothing behind it, a 500, a gateway timeout. Reporting those as a bad
         * password sends someone hunting for a problem that is not theirs - it
         * cost real time in this codebase more than once before the two cases
         * were separated.
         *
         * 429 is the THIRD case, and it is neither: the server is healthy and
         * saying "not so fast". Calling that unreachable makes the reader retry,
         * which is what keeps the window open. See `loginErrors`.
         */
        const failure = await readLoginFailure(res);
        throw Object.assign(new Error(failure.kind), {
          status: res.status,
          retryAfter: failure.retryAfter,
        });
      }
      setUser((await res.json()) as AuthMeResponse);
    },
    [setUser]
  );

  const logout = useCallback(async () => {
    await fetch("/api/cms/v1/auth/logout", {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    leaveOwnerShell();
    setUser(null);
  }, [setUser]);

  // When the API wrapper detects a 401 mid-session, it dispatches this
  // event so we drop the cached user and the next RequireAuth render
  // sends them back to /login.
  useEffect(() => {
    const onInvalidated = () => setUser(null);
    window.addEventListener("foyer:unauthorized", onInvalidated);
    return () =>
      window.removeEventListener("foyer:unauthorized", onInvalidated);
  }, [setUser]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Auth state, or null when rendered outside an <AuthProvider> (e.g. a
 *  component mounted in isolation in a test). Doesn't throw. */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthCtx);
}
